import { GenericAbortSignal } from 'axios';

import { LibraryResponse } from '@/shared/types/Library';
import { PartialVoicePresetProject, VoicePreset } from '@/shared/types/VoicePreset';

import { BASE_URL, LIBRARY_API_URL } from '../constants';
import { del, get, patch, post } from '../requestMethods';
import {
  CreateVoicePresetFromLocProjectPayload,
  CreateVoicePresetPayload,
  GetVoicePresetStatusResponse,
  UpdateVoicePresetPayload,
  UploadVoiceSamplePayload,
  VoiceSampleSettings,
} from './type';

const VOICE_PRESET_BASE_URL = `${BASE_URL}/api/voice_preset/`;

export const VOICE_PRESETS_API = {
  uploadAudioVoiceSample: (data: Omit<UploadVoiceSamplePayload, 'url'>, signal?: GenericAbortSignal) =>
    post<LibraryResponse>(`${LIBRARY_API_URL}/media`, {
      data,
      params: {
        kind: 'audio',
      },
      signal,
      type: 'multipart/form-data',
    }),

  uploadVideoVoiceSample: (data: UploadVoiceSamplePayload, signal?: GenericAbortSignal) =>
    post<LibraryResponse>(`${LIBRARY_API_URL}/media/extract-audio`, {
      data,
      signal,
      type: 'multipart/form-data',
    }),

  createVoicePreset: (data: CreateVoicePresetPayload) => post<VoicePreset>(VOICE_PRESET_BASE_URL, { data }),

  createVoicePresetFromLocProject: ({
    label,
    accent,
    expressiveness,
    isTemporary,
    projectId,
    speakerId,
  }: CreateVoicePresetFromLocProjectPayload) =>
    post<VoicePreset>(`${VOICE_PRESET_BASE_URL}from_project`, {
      data: {
        label,
        is_temporary: isTemporary,
        project_id: projectId,
        similarity_boost: accent,
        speaker_id: speakerId,
        stability: expressiveness,
      },
    }),

  deleteVoicePreset: (id: VoicePreset['id']) => del<{ ok: boolean }>(`${VOICE_PRESET_BASE_URL}${id}`),

  fetchVoiceSampleLink: ({ accent, expressiveness, selectedLanguage, voicePresetId }: VoiceSampleSettings) =>
    get<{ sample_path: string }>(
      `${VOICE_PRESET_BASE_URL}${voicePresetId}/sample?language=${selectedLanguage}&similarity_boost=${accent}&stability=${expressiveness}`,
    ),

  getAllVoicePresets: (spaceId: string) =>
    get<PartialVoicePresetProject[]>(`${VOICE_PRESET_BASE_URL}?space_id=${spaceId}`),
  getVoicePreset: (id: VoicePreset['id'], signal: AbortSignal) =>
    get<VoicePreset>(`${VOICE_PRESET_BASE_URL}${id}`, { signal }),

  getVoicePresetStatus: (ids: string) =>
    get<GetVoicePresetStatusResponse[]>(`${VOICE_PRESET_BASE_URL}statuses?ids=${ids}`),

  updateInstantPresetSettings: ({
    accent,
    expressiveness,
    voicePresetId,
  }: Omit<VoiceSampleSettings, 'selectedLanguage'>) =>
    post<PartialVoicePresetProject>(
      `${VOICE_PRESET_BASE_URL}${voicePresetId}/update_settings_from_project?similarity_boost=${accent}&stability=${expressiveness}`,
    ),

  updateVoicePreset: ({ id, ...rest }: UpdateVoicePresetPayload) =>
    patch<VoicePreset>(`${VOICE_PRESET_BASE_URL}${id}`, { data: rest }),

  updateVoicePresetSettings: ({
    accent,
    expressiveness,
    voicePresetId,
  }: Omit<VoiceSampleSettings, 'selectedLanguage'>) =>
    post<PartialVoicePresetProject>(
      `${VOICE_PRESET_BASE_URL}${voicePresetId}/update_settings?similarity_boost=${accent}&stability=${expressiveness}`,
    ),
};
